
import { useState, useEffect, useCallback, useRef } from 'react';
import { useToast } from "@/hooks/use-toast";
import { createLogger } from "@/utils/logger";
import { supabase } from "@/integrations/supabase/client";

// Import separated modules
import { 
  Coordinates, 
  Permission, 
  STORAGE_KEYS, 
  DEFAULT_CONFIG, 
  LocationConfig 
} from './location/types';

import { useLocationCore } from './location/use-location-core';
import { useLocationPrompts } from './location/use-location-prompts';
import { useLocationSync } from './location/use-location-sync';
import { useLocationUpdater } from './location/use-location-updater';

const logger = createLogger('location');

export const useLocationServices = (config: Partial<LocationConfig> = {}) => {
  const { toast } = useToast();
  const mergedConfig = { ...DEFAULT_CONFIG, ...config };
  
  // Profile tracking refs
  const profileSyncedRef = useRef<boolean>(false);
  const userProfileRef = useRef<any>(null);
  const [hasUserProfile, setHasUserProfile] = useState<boolean>(false);
  const [isResetting, setIsResetting] = useState<boolean>(false);
  
  // Core location hook that manages state
  const locationCore = useLocationCore();
  const { locationState } = locationCore;
  
  // Prompts management
  const promptsManager = useLocationPrompts(locationState, mergedConfig);
  
  // Profile sync manager
  const profileSync = useLocationSync(
    locationCore,
    hasUserProfile,
    profileSyncedRef,
    userProfileRef,
    setHasUserProfile,
    mergedConfig.staleTolerance
  );
  
  // Location updater hook
  const locationUpdater = useLocationUpdater(
    locationCore,
    promptsManager,
    mergedConfig,
    toast
  );
  
  // Debounce function for state changes
  const debounceRef = useRef<number | null>(null);
  const debounce = useCallback((fn: () => void, delay: number = 300) => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = window.setTimeout(() => {
      fn();
      debounceRef.current = null;
    }, delay);
  }, []);
  
  // Reset location services (for testing)
  const resetLocationServices = useCallback(() => {
    // Prevent multiple resets
    if (isResetting) {
      return;
    }
    
    setIsResetting(true);
    
    logger.info('Resetting location services');
    
    // Clear all location-related data from localStorage
    Object.values(STORAGE_KEYS).forEach(key => {
      locationCore.setToStorage(key, null);
    });
    
    // Clear session storage location-related items
    const sessionKeys = Object.keys(sessionStorage);
    sessionKeys.forEach(key => {
      if (key.includes('location_prompt_shown_') || key.includes('distance_filter_prompt_shown_')) {
        sessionStorage.removeItem(key);
      }
    });
    
    // Reset prompt state
    promptsManager.setPromptState({
      lastPromptTime: null,
      promptCount: 0,
      interactionCount: 0,
      cooldownEndTime: null,
      hasShownPrompt: false,
    });
    
    promptsManager.setDoNotShowAgain(false);
    profileSyncedRef.current = false;
    
    // Also clear the database values if the user is authenticated
    if (hasUserProfile) {
      // Explicitly convert the Promise to a full Promise object
      Promise.resolve(
        supabase.from('profiles').update({
          latitude: null,
          longitude: null,
          location_updated_at: null
        }).eq('id', userProfileRef.current?.id)
      )
      .then(() => {
        logger.debug('Cleared location from profile');
        
        // Reset location state AFTER database is cleared
        debounce(() => {
          locationCore.setLocationState({
            coords: null,
            permission: 'prompt',
            isLoading: false,
            error: null,
            permissionTransitioning: false,
            lastUpdated: null,
            initialized: true
          });
          
          setIsResetting(false);
          
          toast({
            title: "Location Settings Reset",
            description: "Your location preferences have been reset."
          });
        }, 500);
      })
      .catch(err => {
        logger.warn('Failed to clear location from profile:', err);
        setIsResetting(false);
        
        toast({
          title: "Reset Partially Completed",
          description: "Your local location settings were reset, but there was an error updating your profile."
        });
      });
    } else {
      // If no user profile, just reset the state
      debounce(() => {
        locationCore.setLocationState({
          coords: null,
          permission: 'prompt',
          isLoading: false,
          error: null,
          permissionTransitioning: false,
          lastUpdated: null,
          initialized: true
        });
        
        setIsResetting(false);
        
        toast({
          title: "Location Settings Reset",
          description: "Your location preferences have been reset."
        });
      }, 500);
    }
    
    logger.info('Location services reset');
  }, [toast, hasUserProfile, locationCore, promptsManager, isResetting, debounce]);

  // Return the public interface
  return {
    locationState,
    requestLocation: locationUpdater.requestLocation,
    trackInteraction: promptsManager.trackInteraction,
    shouldShowLocationPrompt: promptsManager.shouldShowLocationPrompt,
    recordPromptShown: promptsManager.recordPromptShown,
    setMaybeLater: promptsManager.setMaybeLater,
    refreshLocation: locationUpdater.refreshLocation,
    resetLocationServices,
    doNotShowAgain: promptsManager.doNotShowAgain,
    setDoNotShowAgainPreference: promptsManager.setDoNotShowAgainPreference,
    isResetting,
    profileLoaded: profileSync.profileLoaded,
    setHasShownPrompt: (value: boolean) => {
      if (!value) {
        // Only do expensive operations when turning off
        Object.entries({
          [STORAGE_KEYS.PERMISSION]: null,
          [STORAGE_KEYS.COORDS]: null,
          [STORAGE_KEYS.LAST_PROMPT]: null,
          [STORAGE_KEYS.PROMPT_COUNT]: null,
          [STORAGE_KEYS.COOLDOWN]: null,
          [STORAGE_KEYS.TIMESTAMP]: null,
        }).forEach(([key, value]) => locationCore.setToStorage(key, value));
        
        // Reset session storage for prompts
        const keys = Object.keys(sessionStorage);
        keys.forEach(key => {
          if (key.includes('location_prompt_shown_') || key.includes('distance_filter_prompt_shown_')) {
            sessionStorage.removeItem(key);
          }
        });
      }
      
      debounce(() => {
        promptsManager.setPromptState(prev => ({
          ...prev,
          hasShownPrompt: value
        }));
        
        if (!value) {
          locationCore.setLocationState(prev => ({
            ...prev,
            coords: null,
            permission: 'prompt',
            lastUpdated: null,
            error: null,
            permissionTransitioning: false
          }));
        }
      });
      
      // Properly handle the Promise
      Promise.resolve().catch(err => {
        logger.warn('Error in setHasShownPrompt:', err);
      });
    }
  };
};
